import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';

interface SelectOption {
  id: number;
  nombre: string;
}

interface MultiSelectProps {
  options: SelectOption[];
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
  placeholder?: string;
  label?: string;
  className?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedIds,
  onChange,
  placeholder = "Seleccionar opciones...",
  label,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Obtener opciones seleccionadas
  const selectedOptions = options.filter(option => selectedIds.includes(option.id));

  // Toggle selección de una opción
  const toggleOption = (optionId: number) => {
    const newSelectedIds = selectedIds.includes(optionId)
      ? selectedIds.filter(id => id !== optionId)
      : [...selectedIds, optionId];
    
    onChange(newSelectedIds);
  };

  // Remover una opción seleccionada
  const removeOption = (optionId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    const newSelectedIds = selectedIds.filter(id => id !== optionId);
    onChange(newSelectedIds);
  };

  // Limpiar todas las selecciones
  const clearAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange([]);
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      
      <div ref={dropdownRef} className="relative">
        {/* Campo principal */}
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="w-full min-h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent cursor-pointer bg-white flex items-center justify-between"
        >
          <div className="flex-1">
            {selectedOptions.length === 0 ? (
              <span className="text-gray-500">{placeholder}</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {selectedOptions.map((option) => (
                  <span
                    key={option.id}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full"
                  >
                    {option.nombre}
                    <button
                      onClick={(e) => removeOption(option.id, e)}
                      className="hover:bg-green-200 rounded-full p-0.5"
                      type="button"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 ml-2">
            {selectedOptions.length > 0 && (
              <button
                onClick={clearAll}
                className="text-gray-400 hover:text-gray-600 p-1"
                type="button"
                title="Limpiar selección"
              >
                <X size={16} />
              </button>
            )}
            <ChevronDown
              size={16}
              className={`text-gray-400 transition-transform ${
                isOpen ? 'transform rotate-180' : ''
              }`}
            />
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                No hay opciones disponibles
              </div>
            ) : (
              <div className="py-1">
                {options.map((option) => {
                  const isSelected = selectedIds.includes(option.id);
                  return (
                    <div
                      key={option.id}
                      onClick={() => toggleOption(option.id)}
                      className={`px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50 ${
                        isSelected ? 'bg-green-50' : ''
                      }`}
                    >
                      <span className={`text-sm ${
                        isSelected ? 'text-green-800 font-medium' : 'text-gray-900'
                      }`}>
                        {option.nombre}
                      </span>
                      {isSelected && (
                        <Check size={16} className="text-green-600" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;